import { Badge, Button, Card } from '@playerbase/ui';
import type { DiscordGuildChannel } from '@playerbase/types';

// ---------------------------------------------------------------------------
// Watched-channels list. Each row shows the channel under its category
// (`category / # name`) and a Remove action. Rows that the bot can no
// longer see in the guild render as Needs attention - the channel id is
// in watched_channel_ids but no row exists in the discord_guild_channels
// mirror, which the bot maintains.
// ---------------------------------------------------------------------------

type WatchedRow = {
  channelId: string;
  channel: DiscordGuildChannel | null;
};

type WatchedChannelsListProps = {
  rows: WatchedRow[];
  onRemove: (channelId: string) => void;
  busyChannelId: string | null;
};

function channelTypeLabel(type: DiscordGuildChannel['type']): string {
  switch (type) {
    case 'text':
      return 'Text channel';
    case 'forum':
      return 'Forum';
    case 'announcement':
      return 'Announcements';
    case 'thread':
      return 'Thread';
  }
}

export function WatchedChannelsList({
  rows,
  onRemove,
  busyChannelId,
}: WatchedChannelsListProps): JSX.Element {
  return (
    <Card>
      <ul className="divide-y divide-border">
        {rows.map((row) => {
          const channel = row.channel;
          const known = channel !== null;
          const label = known
            ? `${channel.parentName !== null ? `${channel.parentName} / ` : ''}# ${channel.name}`
            : 'Channel no longer visible';
          const sub = known
            ? channelTypeLabel(channel.type)
            : 'The bot can no longer see this channel - it may have been deleted or permissions changed.';

          return (
            <li
              key={row.channelId}
              className="flex items-center justify-between gap-4 px-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium text-text">{label}</span>
                  <Badge tone={known ? 'success' : 'warning'}>
                    {known ? 'Active' : 'Needs attention'}
                  </Badge>
                </div>
                <p className="mt-1 text-xs text-text-muted">{sub}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRemove(row.channelId)}
                disabled={busyChannelId === row.channelId}
              >
                {busyChannelId === row.channelId ? 'Removing…' : 'Remove'}
              </Button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
